import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { StylesService } from '@features/styles/styles.service';
import { Wk8AudioService } from '@features/wk8/core/wk8-audio/wk8-audio.service';
import { Wk8ClawBackComponent } from '@features/wk8/core/wk8-claw-back/wk8-claw-back.component';
import { Wk8ClawFrontComponent } from '@features/wk8/core/wk8-claw-front/wk8-claw-front.component';
import { Wk8ConveyorBeltComponent } from '@features/wk8/core/wk8-conveyor-belt/wk8-conveyor-belt.component';
import { Wk8PanelImageComponent } from '@features/wk8/core/wk8-panel-image/wk8-panel-image.component';
import { Wk8PaperPieceComponent } from '@features/wk8/core/wk8-paper-piece/wk8-paper-piece.component';
import { Wk8PipeBackComponent } from '@features/wk8/core/wk8-pipe-back/wk8-pipe-back.component';
import { Wk8PipeFrontComponent } from '@features/wk8/core/wk8-pipe-front/wk8-pipe-front.component';
import { Wk8ReadFocusDirective } from '@features/wk8/core/wk8-read-focus/wk8-read-focus.directive';
import { Wk8ScreenComponent } from '@features/wk8/core/wk8-screen/wk8-screen.component';
import { waitForMillis, waitForTransitionEnd } from '@shared/delay.utils';
import { getUntransformedBoundingBox } from '@shared/misc.utils';
import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';
import { LpWoordData } from '@typings/lp/generated';

@Component({
  selector: 'wk8-wisselklanken',
  imports: [
    Wk8ConveyorBeltComponent,
    Wk8ClawBackComponent,
    Wk8ClawFrontComponent,
    Wk8PaperPieceComponent,
    Wk8ReadFocusDirective,
    Wk8PanelImageComponent,
    Wk8PipeBackComponent,
    Wk8PipeFrontComponent,
    Wk8ScreenComponent,
  ],
  templateUrl: './wk8-wisselklanken.component.html',
  styleUrl: './wk8-wisselklanken.component.css',
})
export class Wk8WisselklankenComponent implements OnInit, AfterViewInit {
  showScreenAnim = signal(false);
  readonly _wk8TutorService = inject(Wk8TutorService);
  readonly _stylesService = inject(StylesService);

  targetWord = input.required<LpWoordData>();
  scaleMultiplier = input(1);
  finished = output<void>();

  papers = signal<string[]>([]);
  pinned = signal<number[]>([]);
  correct = signal<number[]>([]);
  isChecking = signal(false);
  isSwapping = signal(false);
  isMovingIntoPipe = signal(false);
  isWrong = signal(false);
  hasCompletedReadCheck = signal(true);

  seenKlanken: string[][] = [];

  @ViewChildren('paper') paperEls!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('hook') hookEls!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('panelImage') panelImageComp!: Wk8PanelImageComponent;

  clawSize = computed(() => {
    return 1 * this.scaleMultiplier();
  });
  fontSize = computed(() => {
    return 72 * this.scaleMultiplier();
  });

  readonly #wk8AudioService = inject(Wk8AudioService);

  async ngOnInit(): Promise<void> {
    const klanken = WordToKlankConverter.breakupWord(this.targetWord().word);
    this.papers.set(klanken.map(() => '?'));
    this.seenKlanken = klanken.map(() => []);
  }

  async ngAfterViewInit(): Promise<void> {
    this.syncHooks();
    this.isSwapping.set(true);
    await this.animateOut(this.papers().map((_, index) => index));
    const initialKlanken = this.chanceReplaceWithVariants(WordToKlankConverter.breakupWord(this.targetWord().word));
    await this.changeWord(initialKlanken);
    await this.animateIn(this.papers().map((_, index) => index));
    // Animate screen in after DOM update
    setTimeout(() => this.showScreenAnim.set(true), 10);
    await this._wk8TutorService.showMechanic('wissel_klanken');
    await waitForMillis(500);
    await this.panelImageComp.audioButton?.play();
    this.isSwapping.set(false);
  }

  chanceReplaceWithVariants(klanken: string[]): string[] {
    const klankBecomesVariantChance = 0.6; // 60% chance to become a variant
    const klankenWithSomeVariants = klanken.map((klank) => {
      if (Math.random() < klankBecomesVariantChance) {
        return WordToKlankConverter.getKlankVariantsFor(klank, 1)[0] ?? klank;
      }
      return klank;
    });
    return klankenWithSomeVariants;
  }

  async toggle(index: number) {
    this.#wk8AudioService.playClawGrab();

    this.pinned.update((current) => {
      if (current.includes(index)) {
        return current.filter((i) => i !== index);
      } else {
        return [...current, index];
      }
    });
  }

  async swapIndex(index: number) {
    this.#wk8AudioService.playClawGrab();

    this.isWrong.set(false);
    this.isSwapping.set(true);
    await this.animateOut([index]);
    const correctKlank = WordToKlankConverter.breakupWord(this.targetWord().word)[index];
    const oldKlank = this.papers()[index];

    let newKlank;

    const chanceToGetCorrectKlank = 0.5; // 50% chance to get the correct klank

    if (oldKlank !== correctKlank && (this.seenKlanken[index].length > 3 || Math.random() < chanceToGetCorrectKlank)) {
      newKlank = correctKlank;
    } else {
      newKlank = WordToKlankConverter.getKlankVariantsFor(this.papers()[index], 1, this.seenKlanken[index])[0];
      if (!newKlank) {
        this.seenKlanken[index] = []; // reset seen klanken if no similar klank found
      }
      newKlank = WordToKlankConverter.getKlankVariantsFor(this.papers()[index], 1, [])[0] ?? correctKlank;
    }
    newKlank satisfies string;

    await this.changePaper(index, newKlank);
    await this.animateIn([index]);
    this.isSwapping.set(false);
  }

  public async check() {
    this.isChecking.set(true);
    const targetKlanken = WordToKlankConverter.breakupWord(this.targetWord().word);
    const indices = targetKlanken.map((klank, index) => ({ klank, isCorrect: klank === this.papers()[index], index }));
    const areAllCorrect = indices.every(({ isCorrect }) => isCorrect);
    this.correct.set(indices.filter(({ isCorrect }) => isCorrect).map(({ index }) => index));
    this.pinned.set(indices.filter(({ isCorrect }) => isCorrect).map(({ index }) => index));
    if (areAllCorrect) {
      this.#wk8AudioService.playActivityCorrect();
      await this.moveIntoPipe();
    } else {
      this.isWrong.set(true);
      await this._wk8TutorService.incorrect();
    }

    await waitForMillis(500);

    if (areAllCorrect) {
      this.finished.emit();
    }
    this.isChecking.set(false);
  }

  private syncHooks() {
    for (let i = 0; i < this.paperEls.length; i++) {
      const paperEl = this.paperEls.get(i)?.nativeElement;
      const hookEl = this.hookEls.get(i)?.nativeElement.children[0] as HTMLElement;
      if (!paperEl || !hookEl) continue;

      const paperRect = paperEl.getBoundingClientRect();
      const hookRect = getUntransformedBoundingBox(hookEl);
      const prevTransform = hookEl.style.transform;
      let yTranslation: number | undefined;
      if (prevTransform) {
        // only edit x offset
        const match = prevTransform.match(/translate\([^,]+,\s*([^)]+)\)/);
        const prevYTranslation = parseFloat(match![1]);
        yTranslation = prevYTranslation;
      }
      hookEl.style.transform = `translate(${paperRect.left - hookRect.x + paperRect.width / 2}px, ${yTranslation ?? paperRect.top - hookRect.y}px)`;
    }
  }

  private async moveIntoPipe() {
    this.isMovingIntoPipe.set(true);
    this.#wk8AudioService.playConveyor();
    await waitForMillis(1000); // NOTE: we need to wait a bit for the animation to finish, for now this is hardcoded
    this.#wk8AudioService.playPipeSuck();
  }

  private async animateOut(indices: number[]) {
    if (indices.length > 0) this.#wk8AudioService.playClawMoveUp();
    await Promise.all(
      indices.map(async (index) => {
        const paperEl = this.paperEls.get(index)!.nativeElement.parentElement!;
        const hookEl = this.hookEls.get(index)!.nativeElement;

        paperEl.classList.add('paper-hide');
        hookEl.classList.remove('claw-visible');
        await waitForTransitionEnd(hookEl);
      }),
    );
  }

  private async changeWord(klanken: string[], mask?: number[]) {
    this.papers.set(this.papers().map((klank, index) => ((mask?.includes(index) ?? true) ? klanken[index] : klank)));
    this.papers().map((klank, index) => this.seenKlanken[index].push(klank));
    await waitForMillis(100);
    this.syncHooks();
  }

  private async changePaper(index: number, newKlank: string) {
    this.papers.update((current) => {
      const newPapers = [...current];
      newPapers[index] = newKlank;
      return newPapers;
    });
    this.seenKlanken[index].push(newKlank);
    await waitForMillis(100);
    this.syncHooks();
  }

  private async animateIn(indices: number[]) {
    if (indices.length > 0) this.#wk8AudioService.playClawMoveDown();
    await Promise.all(
      indices.map(async (index) => {
        const paperEl = this.paperEls.get(index)!.nativeElement.parentElement!;
        const hookEl = this.hookEls.get(index)!.nativeElement;

        hookEl.classList.add('claw-visible');
        paperEl.classList.remove('paper-hide');
        await waitForTransitionEnd(hookEl);
      }),
    );
    await waitForMillis(100);
  }
}
